import { AVATAR_LOOKS, bowOf, CLASS_IDS, classColor, ITEM_LOOKS, itemDef, ITEMS, PROCEDURAL_ITEMS } from "@eldrun/content";
import { ItemDrop, MatchState } from "@eldrun/protocol";
import { Mesh, PerspectiveCamera, PlaneGeometry, Scene, Vector3, type WebGLRenderer } from "three";
import { FrameTimer } from "../engine/frame-loop.ts";
import { FrameStats } from "../engine/frame-stats.ts";
import { StatsHud } from "../engine/stats-hud.ts";
import { AVATAR_CLIPS } from "../assets/avatars.generated.ts";
import { swingClipTime } from "../game/avatar-state.ts";
import { createAvatarView, ProceduralAvatar, type AvatarView } from "../game/avatar-view.ts";
import { SkinnedAvatar } from "../game/skinned-avatar.ts";
import type { AvatarLibrary } from "../render/avatar-library.ts";
import { DropLayer } from "../game/drop-layer.ts";
import { heldMesh } from "../game/held-item.ts";
import { swingArm } from "../game/swing.ts";
import { applyAtmosphere, createAtmosphereUniforms, sampleAtmosphere } from "../render/atmosphere.ts";
import { ItemIcons } from "../render/item-icons.ts";
import { PostFx } from "../render/post/post-fx.ts";
import { countPrograms } from "../render/post/post-model.ts";
import { itemModel } from "../render/item-models.ts";
import type { ModelLibrary } from "../render/model-library.ts";
import { createFoliageMaterial } from "../render/shaders/foliage.ts";
import { toonMaterial } from "../render/shaders/toon.ts";

export interface ItemViewer {
  stats(): Record<string, unknown>;
  stop(): void;
}

export type ItemViewerView = "geral" | "mao" | "chao";

export interface ItemViewerOptions {
  /** Item na mão do primeiro boneco da fila (a primeira pessoa saiu na F55). */
  item: string;
  /** `geral` (bonecos, chão e ícones), `mao` (os bonecos de perto) ou `chao` (os itens no chão de perto). */
  view: ItemViewerView;
  phase: number;
  /** Mostra a grade de ícones (`&icones=0` esconde, para as capturas da cena). */
  icons: boolean;
}

/** Na mão dos bonecos (terceira pessoa), golpeando de tempos em tempos: uma forma comprida de cada e a tocha (F48). */
const HELD = ["machado-ferro", "picareta-prata", "espada-raio", "lanca-ferro", "arco-prata", "marreta-trovejante", "facao-pederneira", "madeira", "tocha"];

/**
 * No chão: 16 tipos com modelo (todas as formas, com a tocha da F48, e as tintas do raio e da madeira),
 * mais perto da câmera, e dois procedurais mais longe, que passam do teto de tipos e viram cubos.
 */
const GROUND = [
  "machado-madeira",
  "machado-raio",
  "picareta-pedra",
  "marreta-ferro",
  "porrete",
  "lanca-prata",
  "facao-pederneira",
  "espada-prata",
  "arco-galho",
  "tocha",
  "flecha-ferro",
  "madeira",
  "lingote-raio",
  "minerio-ferro",
  "corda",
  "pederneira",
];
const GROUND_EXTRA = ["banana", "couro"];

const CAMERAS: Record<ItemViewerView, { position: readonly [number, number, number]; target: readonly [number, number, number] }> = {
  geral: { position: [0, 2.4, 5.6], target: [0, 0.7, -1] },
  mao: { position: [0, 1.35, 0.1], target: [0, 1.05, -2.6] },
  chao: { position: [0, 1.15, 2.4], target: [0, 0.25, -0.1] },
};

/**
 * Vitrine dos itens (`?debug=itens`, F47, sem servidor): bonecos com ferramentas e armas na mão
 * (terceira pessoa, golpeando), os itens no chão girando e flutuando pela `DropLayer` (16 tipos com
 * modelo e dois que viram cubo), o item escolhido (`&item=`) na mão do primeiro boneco e a grade de
 * ícones de todos os itens com modelo e dos procedurais. `&modelos=0` mostra a reserva procedural.
 */
export function startItemViewer(renderer: WebGLRenderer, library: ModelLibrary, options: ItemViewerOptions, avatarLibrary?: AvatarLibrary): ItemViewer {
  const scene = new Scene();
  const atmosphere = createAtmosphereUniforms();
  const sample = sampleAtmosphere(options.phase);
  scene.background = sample.horizon;
  const camera = new PerspectiveCamera(75, 1, 0.1, 300);
  const framing = CAMERAS[options.view];
  camera.position.fromArray(framing.position);
  camera.lookAt(new Vector3().fromArray(framing.target));
  scene.add(camera);
  const ground = new Mesh(new PlaneGeometry(60, 60), toonMaterial(atmosphere, "#7FA34E", 0));
  ground.rotation.x = -Math.PI / 2;
  scene.add(ground);
  const foliage = createFoliageMaterial(atmosphere, { outline: true });
  const timer = new FrameTimer();
  const hud = new StatsHud(document.body);
  // F52: a contagem do quadro e o pós-processamento (`&pos=`; padrão, o Médio).
  const counter = new FrameStats(renderer.info);
  const unwatch = counter.watch(scene);
  const post = new PostFx(renderer);
  post.bind(atmosphere);
  const resize = () => {
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
  };
  window.addEventListener("resize", resize);
  resize();

  // Jogadores em fila, de perfil (olhando para +x, a mão direita para a câmera), cada um com um item:
  // os avatares animados (F49), um personagem por cor de classe, ou os bonecos procedurais sem o pacote deles.
  let firstHeld: Mesh | undefined;
  const rigs: AvatarView[] = HELD.map((item, i) => {
    const id = i === 0 ? options.item : item;
    const avatar = createAvatarView(avatarLibrary, atmosphere, classColor(CLASS_IDS[i % CLASS_IDS.length]!), AVATAR_LOOKS[i % AVATAR_LOOKS.length]!.character, i);
    avatar.root.position.set((i - (HELD.length - 1) / 2) * 1.4, 0, -2.6);
    avatar.root.rotation.y = -Math.PI / 2;
    const mesh = heldMesh(id, library, foliage, avatar.heldView);
    if (i === 0) firstHeld = mesh;
    avatar.setHeld(mesh, id);
    scene.add(avatar.root);
    return avatar;
  });

  // Itens no chão: os 16 com modelo em duas fileiras perto da câmera, os procedurais atrás.
  const state = new MatchState();
  [...GROUND, ...GROUND_EXTRA].forEach((id, i) => {
    const drop = new ItemDrop();
    drop.itemId = id;
    drop.qty = 1;
    const extra = i >= GROUND.length;
    drop.x = extra ? (i - GROUND.length - 0.5) * 1.2 : ((i % 8) - 3.5) * 0.9;
    drop.z = extra ? -6 : i < 8 ? 0.6 : -0.4;
    drop.y = 0;
    state.drops.set(String(i + 1), drop);
  });
  const drops = new DropLayer(scene, foliage, { library });

  // Grade de ícones: todos os itens com modelo e os procedurais do mesmo grupo, mais alguns outros.
  const icons = new ItemIcons(renderer, library);
  const iconIds = [...Object.keys(ITEM_LOOKS), ...PROCEDURAL_ITEMS, ...ITEMS.filter((i) => i.kind === "comida" || i.kind === "armadura").slice(0, 4).map((i) => i.id)];
  const grid = document.createElement("div");
  grid.id = "vitrine-icones";
  grid.setAttribute("aria-label", "Ícones dos itens");
  Object.assign(grid.style, {
    position: "fixed",
    right: "8px",
    top: "8px",
    display: "grid",
    gridTemplateColumns: "repeat(9, 44px)",
    gap: "4px",
    padding: "6px",
    background: "rgba(14, 26, 20, 0.72)",
    borderRadius: "6px",
    zIndex: "10",
  });
  const iconStart = performance.now();
  for (const id of iconIds) {
    const cell = document.createElement("div");
    cell.className = "icone";
    cell.dataset["item"] = id;
    cell.title = itemDef(id)?.name ?? id;
    Object.assign(cell.style, { width: "44px", height: "44px", backgroundImage: `url(${icons.url(id)})`, backgroundSize: "contain", backgroundColor: "rgba(255, 255, 255, 0.08)", borderRadius: "4px" });
    grid.appendChild(cell);
  }
  const iconMs = performance.now() - iconStart;
  // `display: grid` vence o atributo `hidden`: esconde pelo estilo.
  if (!options.icons) grid.style.display = "none";
  document.body.appendChild(grid);

  const modeled = Object.keys(ITEM_LOOKS).filter((id) => itemModel(id, 0, library) !== undefined).length;
  let last = performance.now();
  renderer.setAnimationLoop((now: number) => {
    counter.begin();
    const dt = Math.min(0.1, (now - last) / 1000);
    timer.push(now - last);
    last = now;
    const t = now / 1000;
    // Golpe de 0,7 s a cada 2 s, defasado por boneco.
    const progress = (offset: number) => {
      const p = ((t + offset) % 2) / 0.7;
      return p < 1 ? Math.max(p, 1e-3) : 0;
    };
    rigs.forEach((avatar, i) => {
      const p = progress(i * 0.25);
      if (avatar instanceof ProceduralAvatar) avatar.swing(swingArm(p));
      if (!(avatar instanceof SkinnedAvatar)) return;
      // O arco puxa a corda (0,9 s) e segura; o resto golpeia com o impacto no do servidor.
      if (bowOf(i === 0 ? options.item : HELD[i]!)) avatar.showClip("arco", Math.min(((t + i * 0.25) % 2) * 1.4, AVATAR_CLIPS.arco.duration - 1e-3));
      else avatar.showClip(p > 0 ? "golpe" : "parado", p > 0 ? swingClipTime(p, AVATAR_CLIPS.golpe) : (t % AVATAR_CLIPS.parado.duration));
    });
    applyAtmosphere(atmosphere, sample, t);
    drops.update(state, t, camera.position.x, camera.position.z);
    post.render(scene, camera, dt, counter);
    const f = counter.end();
    hud.update(now, () => [
      `Eldrun · itens · ${timer.fps.toFixed(0)} fps · draw calls ${f.drawCalls} (+${f.postCalls} do pós)`,
      `modelos ${modeled}/${Object.keys(ITEM_LOOKS).length} · no chão ${drops.stats().kinds} tipos + ${drops.stats().cubes} cubos · na mão: ${options.item}`,
    ]);
  });

  return {
    stats: () => ({
      fps: timer.fps,
      drawCalls: counter.last.drawCalls,
      frameCalls: counter.last.frameCalls,
      programs: countPrograms(renderer.info.programs).scene,
      post: post.stats(counter.last),
      items: Object.keys(ITEM_LOOKS).length,
      modeled,
      icons: grid.childElementCount,
      iconMs: Math.round(iconMs),
      held: { avatars: rigs.length, model: firstHeld?.userData["model"] === true, item: options.item },
      drops: { total: state.drops.size, ...drops.stats() },
    }),
    stop() {
      renderer.setAnimationLoop(null);
      post.dispose();
      unwatch();
      window.removeEventListener("resize", resize);
      for (const avatar of rigs) avatar.dispose();
      drops.dispose();
      icons.dispose();
      grid.remove();
      hud.remove();
      foliage.dispose();
      ground.geometry.dispose();
      (ground.material as { dispose(): void }).dispose();
    },
  };
}
